import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { io, type Socket } from "socket.io-client";

interface SocketContextValue {
  socket: Socket | null;
  isConnected: boolean;
  fallbackToPolling: boolean;
}

const SocketContext = createContext<SocketContextValue>({
  socket: null,
  isConnected: false,
  fallbackToPolling: true,
});

export function useSocket() {
  return useContext(SocketContext);
}

const SOCKET_URL = import.meta.env.VITE_API_URL || "http://localhost:4001";
const MAX_RECONNECT_ATTEMPTS = 10;

export function SocketProvider({ children }: { children: ReactNode }) {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [fallbackToPolling, setFallbackToPolling] = useState(false);
  const reconnectAttemptsRef = useRef(0);

  useEffect(() => {
    async function getToken(): Promise<string | null> {
      try {
        // Wait for Clerk to be available
        const clerk = (window as any).Clerk;
        if (!clerk?.session) return null;
        return await clerk.session.getToken();
      } catch {
        return null;
      }
    }

    async function connect() {
      const token = await getToken();
      if (!token) {
        // Clerk not ready yet — retry after a short delay
        setTimeout(connect, 2000);
        return;
      }

      const socket = io(SOCKET_URL, {
        auth: { token },
        transports: ["websocket", "polling"],
        autoConnect: true,
        reconnection: true,
        reconnectionAttempts: MAX_RECONNECT_ATTEMPTS,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 10000,
      });

      socket.on("connect", () => {
        setIsConnected(true);
        setFallbackToPolling(false);
        reconnectAttemptsRef.current = 0;
      });

      socket.on("disconnect", () => {
        setIsConnected(false);
      });

      socket.on("connect_error", async (err) => {
        console.warn("Socket connection error:", err.message);
        reconnectAttemptsRef.current += 1;

        // If auth error, try refreshing the token
        if (
          err.message === "Authentication failed" ||
          err.message === "Authentication required"
        ) {
          const newToken = await getToken();
          if (newToken && socket.auth) {
            (socket.auth as any).token = newToken;
          }
        }

        if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
          setFallbackToPolling(true);
        }
      });

      setSocket(socket);
      socketInstanceRef = socket;
    }

    let socketInstanceRef: Socket | null = null;

    connect();

    // Periodically refresh the auth token (every 50s, Clerk tokens expire ~60s)
    const tokenRefreshInterval = setInterval(async () => {
      if (!socketInstanceRef) return;
      const newToken = await getToken();
      if (newToken && socketInstanceRef.auth) {
        (socketInstanceRef.auth as any).token = newToken;
      }
    }, 50000);

    return () => {
      clearInterval(tokenRefreshInterval);
      if (socketInstanceRef) {
        socketInstanceRef.disconnect();
        socketInstanceRef = null;
        setSocket(null);
      }
    };
  }, []);

  return (
    <SocketContext.Provider
      value={{
        socket,
        isConnected,
        fallbackToPolling,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
}
